import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHead from './SEOHead';
import StarryBackground from './StarryBackground';
import { getProfileThumbnail } from '../utils/thumbnailGenerator';
import { useI18n } from '../i18n';

const ProfilePage = ({ primaryAction, secondaryAction, isAppInstalled }) => {
  const { nickname } = useParams();
  const { t } = useI18n();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        // Simula chiamata API per metadati profilo pubblici (per SEO)
        // In futuro puoi chiamare il tuo backend per ottenere dati pubblici del profilo
        const response = await fetch(`https://your-api.com/profile-metadata/${nickname}`);
        
        if (response.ok) {
          const data = await response.json();
          setProfileData(data);
        } else {
          // Fallback: dati generici
          setProfileData({
            nickname: nickname,
            displayName: `@${nickname}`,
            bio: 'Utente WanderBuddy',
            trips: 0,
            level: 'Esploratore'
          });
        }
      } catch (err) {
        console.log('Error fetching profile metadata:', err);
        // Fallback silenzioso
        setProfileData({
          nickname: nickname,
          displayName: `@${nickname}`,
          bio: 'Esploratore WanderBuddy',
          trips: 'Vari',
          level: 'Viaggiatore'
        });
      } finally {
        setLoading(false);
      }
    };

    if (nickname) {
      fetchProfileData();
    } else {
      setLoading(false);
    }
  }, [nickname]);

  const pageTitle = `${profileData?.displayName || `@${nickname}`} | WanderBuddy`;
  const pageDescription = t('profile.subtitle');

  if (loading) {
    return (
      <>
        <StarryBackground />
        <div className="sora-layout">
          {/* Header capsule */}
          <motion.header 
            className="sora-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="sora-header-content">
              <div className="sora-logo-text">
                <img src="/wanderbuddy-white.png" alt="WanderBuddy" style={{ height: '32px', width: 'auto' }} />
              </div>
              <div className="sora-header-actions">
                <motion.a
                  href="https://apps.apple.com/app/id6737308693"
                  className="sora-btn sora-btn-download"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Download
                </motion.a>
                <motion.button
                  className="sora-btn sora-btn-login"
                  disabled
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                >
                  Login
                </motion.button>
              </div>
            </div>
          </motion.header>

          <motion.main 
            className="sora-main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="sora-app-icon">
              <div style={{ 
                width: '140px', height: '140px', background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)', borderRadius: '32px', display: 'flex',
                alignItems: 'center', justifyContent: 'center', fontSize: '4rem',
                border: '1px solid rgba(255,255,255,0.2)'
              }}>👤</div>
            </div>
            <h1 className="sora-title">{t('profile.open_in_app')}</h1>

            {/* App Store badge */}
            <motion.div 
              className="sora-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <motion.a
                href="https://apps.apple.com/app/id6737308693"
                className="sora-appstore-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <img 
                  src="/app-store-badge.svg" 
                  alt="Download on the App Store" 
                  style={{ height: '48px', width: 'auto' }}
                />
              </motion.a>
            </motion.div>
          </motion.main>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead 
        title={pageTitle}
        description={pageDescription}
        image={getProfileThumbnail(profileData)}
      />

      <StarryBackground />

      <div className="sora-layout">
        {/* Header capsule */}
        <motion.header 
          className="sora-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="sora-header-content">
            <div className="sora-logo-text">
              <img src="/wanderbuddy-white.png" alt="WanderBuddy" style={{ height: '32px', width: 'auto' }} />
            </div>
            <div className="sora-header-actions">
              <motion.a
                href="https://apps.apple.com/app/id6737308693"
                className="sora-btn sora-btn-download"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Download
              </motion.a>
              <motion.button
                className="sora-btn sora-btn-login"
                disabled
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
              >
                Login
              </motion.button>
            </div>
          </div>
        </motion.header>

        <motion.div 
          className="sora-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
        {/* Avatar/Icon */}
        <motion.div 
          className="app-logo"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          👤
        </motion.div>
        
        {/* Nome utente */}
        <motion.h1 
          className="title"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {profileData?.displayName || `@${nickname}`}
        </motion.h1>
        
        <motion.p 
          className="subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {t('profile.subtitle')}
        </motion.p>

        {/* App Store badge */}
        <motion.div 
          className="sora-actions"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          style={{ marginBottom: '1rem' }}
        >
          <motion.a
            href="https://apps.apple.com/app/id6737308693"
            className="sora-appstore-btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img 
              src="/app-store-badge.svg" 
              alt="Download on the App Store" 
              style={{ height: '48px', width: 'auto' }}
            />
          </motion.a>
        </motion.div>

        {/* Profile preview card */}
        {profileData && (
          <motion.div 
            className="device-context"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ 
                width: '60px', 
                height: '60px', 
                background: 'var(--primary-gradient)', 
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem'
              }}>
                🧳
              </div>
              <div style={{ textAlign: 'left', flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                  {profileData.displayName}
                </div>
                <div style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
                  {profileData.level} • {profileData.trips} itinerari
                </div>
                <div style={{ color: 'var(--text-light)', fontSize: '0.8rem' }}>
                  {profileData.bio}
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
              <strong>👀 Cosa puoi vedere:</strong>
              <br />
              • Itinerari pubblici e thread di viaggio
              <br />
              • Livello esperienza e statistiche
              <br />
              • Possibilità di seguire l'utente
            </div>
          </motion.div>
        )}

        {/* Azioni */}
        <motion.div 
          style={{ margin: '2rem 0' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          {/* Pulsante principale come <a> per supportare Universal Links */}
          <motion.a
            href={primaryAction.href}
            className={`btn ${primaryAction.primary ? 'btn-primary' : 'btn-secondary'} pulse`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{ 
              pointerEvents: primaryAction.disabled ? 'none' : 'auto',
              opacity: primaryAction.disabled ? 0.6 : 1,
              textDecoration: 'none',
              display: 'inline-block'
            }}
          >
            {primaryAction.disabled && <div className="loading-spinner"></div>}
            {primaryAction.text}
          </motion.a>
          
          {secondaryAction && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <motion.a
                href={secondaryAction.href}
                className="btn btn-ghost"
                target={secondaryAction.external ? '_blank' : '_self'}
                rel={secondaryAction.external ? 'noopener noreferrer' : ''}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{ textDecoration: 'none', display: 'inline-block' }}
              >
                {secondaryAction.text}
              </motion.a>
            </motion.div>
          )}
        </motion.div>

        {/* Info social */}
        <motion.div 
          className="alert alert-info"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <strong>🌟 Funzionalità social:</strong>
          <br />
          • Segui viaggiatori con i tuoi stessi interessi
          <br />
          • Scopri itinerari autentici da persone reali
          <br />
          • Commenta e salva i viaggi che ti piacciono
        </motion.div>

        {/* Link back */}
        <motion.div 
          style={{ marginTop: '2rem' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
        >
          <a 
            href="/"
            style={{ 
              color: 'var(--primary-color)', 
              textDecoration: 'none',
              fontSize: '0.9rem'
            }}
          >
            {t('common.back_home')}
          </a>
        </motion.div>

        {/* Footer */}
        <motion.div 
          className="footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <p>{t('common.footer')}</p>
        </motion.div>
        </motion.div>
      </div>
    </>
  );
};

export default ProfilePage;
